import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  SvgIcon,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { api } from "../api";
import usePageTitle from "../hooks/usePageTitle";
import AppShell from "./AppShell";
import ImageWithFallback from "./ImageWithFallback";
import { buildItemUrl } from "../utils/itemUrl";
import { formatPrice, getPriceDollars } from "../utils/price";
import { usePriceVisibility } from "../contexts/PriceVisibilityContext";

const quickLinks = [
  { title: "Pokemon Cards", path: "/search?category=Pokemon", image: "/logos/quicklinkspics/poke.jpg" },
  { title: "Sports Cards", path: "/search?category=Sports", image: "/logos/quicklinkspics/cards.webp" },
  { title: "Funko", path: "/search?category=Funko", image: "/logos/quicklinkspics/funko.jpg" },
  { title: "Memorabilia", path: "/search?category=Memorabilia", image: "/logos/quicklinkspics/memorabilia.jpg" },
  { title: "Comics", path: "/search?category=Comics", image: "/logos/quicklinkspics/comics.jpg" },
  { title: "LEGO", path: "/search?category=LEGO", image: "/logos/quicklinkspics/lego.jpg" },
  { title: "Blind Box", path: "/search?category=Blind%20Box", image: "/logos/quicklinkspics/blindbox.jpg" },
  { title: "Games", path: "/search?category=Games", image: "/logos/quicklinkspics/games.jpg" },
];

const setsLinks = [
  {
    title: "Pokemon Sets",
    detail: "Browse modern and vintage Pokemon releases.",
    path: "/sets/pokemon",
    image: "/sets/pokemon sets.webp",
  },
  {
    title: "One Piece Sets",
    detail: "Explore One Piece releases collectors are watching.",
    path: "/sets/one-piece",
    image: "/sets/one piece sets.webp",
  },
  {
    title: "Football Card Sets",
    detail: "Browse football set pages and individual football card releases.",
    path: "/sets/football",
    image: "/sets/football%20sets.jpg",
  },
  {
    title: "Hockey Card Sets",
    detail: "Browse hockey set pages grouped from the database.",
    path: "/sets/hockey",
    image: "/sets/hockey%20sets.avif",
  },
  {
    title: "Basketball Card Sets",
    detail: "Browse basketball set pages and their loaded items.",
    path: "/sets/basketball",
    image: "/sets/basketball%20sets.webp",
  },
  {
    title: "Soccer Card Sets",
    detail: "Browse soccer set pages and current card inventory.",
    path: "/sets/soccer",
    image: "/sets/soccer%20sets.jpg",
  },
];

const trendingEvents = [
  {
    title: "World Cup Collectibles",
    detail: "World Cup momentum is lifting soccer cards, sealed products, memorabilia, LEGO sets, and international superstar collectibles.",
    path: "/events/road-to-world-cup",
  },
  {
    title: "One Piece OP-17",
    detail: "OP-17: The World's Strongest Warriors is now live on The Collector Stop, with collectors able to explore the latest leaders, alternate arts, manga cards, and other chase releases.",
    path: "/one-piece/sets/op17",
  },
  {
    title: "Carolina Hurricanes Stanley Cup Champions",
    detail: "The Carolina Hurricanes are Stanley Cup Champions, with collectors watching Canes cards, Young Guns, autographs, jerseys, memorabilia, and championship-driven demand.",
    path: "/search?category=Sports&q=Carolina%20Hurricanes%7CCanes&qMode=any&event=hurricanes-stanley-cup-champions",
  },
  {
    title: "New York Knicks NBA Champions",
    detail: "The New York Knicks are NBA Champions, with collectors watching Knicks stars, rookies, parallels, jerseys, autographs, and championship-driven market movement.",
    path: "/search?category=Sports&q=New%20York%20Knicks%7CNY%20Knicks%7CKnicks&qMode=any&event=knicks-nba-champions",
  },
  {
    title: "Pokemon's 30th Anniversary",
    detail: "Pokemon's 30th Anniversary is bringing renewed collector attention to First Partner products, anniversary releases, sealed items, and nostalgic characters.",
    path: "/events/pokemon-30th-anniversary",
  },
  {
    title: "Pokémon TCG: 30th Celebration",
    detail: "Pokémon's 30th Celebration is now live on The Collector Stop, featuring anniversary favorites, Pikachu Rare cards, Illustration Rares, Special Illustration Rares, Futuristic Rares, and the special Mew G/R/B cards.",
    path: "/pokemon/sets/30th-celebration",
  },
];

const newReleases = [
  {
    title: "Pokémon TCG: 30th Celebration",
    subtitle: "30th Celebration is now live on The Collector Stop, with Pikachu Rare cards, Illustration Rares, Special Illustration Rares, Futuristic Rares, and anniversary chase cards ready to explore.",
    image: "/sets/pokemon/30th%20Celebration.jpg",
    path: "/pokemon/sets/30th-celebration",
  },
  {
    title: "LEGO World Cup Sets",
    subtitle: "World Cup momentum is carrying into LEGO, with soccer-themed sets giving sports and LEGO collectors another crossover category to watch.",
    image: "/images/releases/world cup.webp"
  },
  {
    title: "2025-26 Upper Deck Extended Series Hockey",
    subtitle: "Extended Series Hockey closes out the flagship Upper Deck hockey cycle, with collectors watching Young Guns, inserts, parallels, and late-season rookie demand.",
    image: "/images/releases/extended.webp"
  },
  {
    title: "One Piece OP-17",
    subtitle: "OP-17: The World's Strongest Warriors is now live, with leaders, alternate arts, manga cards, and other chase releases available to explore.",
    image: "/sets/one%20piece/op17.png",
    path: "/one-piece/sets/op17",
  }
];


const upcomingItems = [
  {
    title: "Pokémon First Partner Collection Vol. 3",
    when: "Upcoming anniversary product",
    note: "Volume 3 adds another anniversary-focused Pokémon product window, with collectors watching demand across starter Pokémon and sealed boxes."
  },
  {
    title: "FIFA x Fanatics / Topps Collectibles",
    when: "World Cup cycle",
    note: "FIFA and Fanatics announced a major collectibles partnership tied to World Cup momentum, making soccer cards and memorabilia a category to watch."
  }
];

const communityFeatures = [
  {
    title: "Collector Connections",
    detail: "Connect with collectors you know and build a focused collecting circle.",
  },
  {
    title: "Collection Activity",
    detail: "See recent pickups, wishlist adds, and collection milestones from friends.",
  },
  {
    title: "Collector Profiles",
    detail: "View public collections, wishlists, and recent additions from other collectors.",
  },
];

const panelSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#ffffff",
  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
};

const interactiveCardSx = {
  borderRadius: 2,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#f9fbfd",
  transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease",
  cursor: "pointer",
  textDecoration: "none",
  color: "inherit",
  display: "block",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 16px 30px rgba(15, 23, 42, 0.12)",
    borderColor: "rgba(18, 89, 156, 0.20)",
    backgroundColor: "#ffffff",
  },
};

const homeIconPaths = {
  collection: "M20 6h-4V4c0-1.1-.9-2-2-2h-4C8.9 2 8 2.9 8 4v2H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2M10 4h4v2h-4v-2m10 15H4V8h16v11Z",
  sparkle: "m19 9 1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9Zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5ZM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15Z",
  verified: "m23 12-2.44-2.79.34-3.69-3.61-.82-1.89-3.2L12 3.24 8.6 1.8 6.71 5 3.1 5.81l.34 3.7L1 12l2.44 2.79-.34 3.7 3.61.81 1.89 3.2 3.4-1.47 3.4 1.46 1.89-3.2 3.61-.82-.34-3.69L23 12Zm-12.91 4.72-3.8-3.81 1.48-1.48 2.32 2.33 5.85-5.87 1.48 1.48-7.33 7.35Z",
  privacy: "M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm6-9h-1V6A5 5 0 0 0 7 6v2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2ZM9 6a3 3 0 0 1 6 0v2H9V6Zm9 14H6V10h12v10Z",
  search: "M9.5 3a6.5 6.5 0 1 0 3.98 11.64L19.85 21 21 19.85l-6.36-6.37A6.5 6.5 0 0 0 9.5 3Zm0 2a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Z",
  create: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2Z",
  spreadsheet: "M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2Zm0 4h-4V5h4v2Zm-6-2v2H5V5h8ZM5 9h8v3H5V9Zm0 5h8v5H5v-5Zm10 5v-5h4v5h-4Zm4-7h-4V9h4v3Z",
  app: "M4 4h7v7H4V4Zm9 0h7v7h-7V4ZM4 13h7v7H4v-7Zm9 0h7v7h-7v-7Z",
  arrow: "M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8Z",
};

function HomeIcon({ path, ...props }) {
  return (
    <SvgIcon {...props}>
      <path d={path} />
    </SvgIcon>
  );
}

function SectionBlock({ title, subtitle, action, children, sx }) {
  return (
    <Paper elevation={0} sx={{ ...panelSx, ...sx }}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
          mb: 2,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 800, color: "primary.main", mb: 0.5 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 760 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
      {children}
    </Paper>
  );
}

function PlaceholderMedia({ label, height = 120 }) {
  return (
    <Box
      sx={{
        height,
        borderRadius: 1.75,
        border: "1px solid",
        borderColor: "rgba(12, 35, 64, 0.08)",
        backgroundColor: "#eef3f8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "text.secondary",
        fontWeight: 700,
        letterSpacing: 0.3,
      }}
    >
      {label}
    </Box>
  );
}

function formatCurrency(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "TBD";
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatChange(item) {
  const amount = Number(item?.change_amount);
  const pct = Number(item?.change_pct);
  if (!Number.isFinite(amount)) return "";

  const amountLabel = `${amount > 0 ? "+" : ""}${formatCurrency(amount)}`;
  if (!Number.isFinite(pct)) return amountLabel;
  return `${amountLabel} (${pct > 0 ? "+" : ""}${pct.toFixed(1)}%)`;
}

function formatTrendingPrice(item) {
  const directPrice = Number(item?.price);
  if (Number.isFinite(directPrice)) {
    return formatPrice({ price: directPrice.toFixed(2) }) || "TBD";
  }

  const normalized = getPriceDollars(item);
  return normalized == null ? "TBD" : formatPrice({ price: normalized.toFixed(2) }) || "TBD";
}

function IntroFeaturedSection() {
  const collectorBenefits = [
    {
      title: "Everything in One Place",
      detail: "Track cards, LEGO, Funko, memorabilia, comics, games, and more.",
      icon: homeIconPaths.collection,
    },
    {
      title: "Missing an Item?",
      detail: "Add it yourself manually or generate it instantly with AI.",
      icon: homeIconPaths.sparkle,
    },
    {
      title: "Accuracy Comes First",
      detail: "Every submission is reviewed by AI before going live, then checked by our team for accuracy.",
      icon: homeIconPaths.verified,
    },
    {
      title: "Track Without a Subscription",
      detail: "Create an account and use the collection tracker without a recurring subscription.",
      icon: homeIconPaths.verified,
    },
  ];

  const ctaButtonSx = {
    fontWeight: 800,
    px: 2.25,
    boxShadow: "none",
    backgroundColor: "#ffffff",
    color: "#0b1220",
    "&:hover": {
      boxShadow: "none",
      backgroundColor: "rgba(255,255,255,0.92)",
    },
  };

  const socialButtonSx = {
    fontWeight: 800,
    px: 1.6,
    py: 0.85,
    borderRadius: 999,
    textTransform: "none",
    color: "#ffffff",
    borderColor: "rgba(255,255,255,0.20)",
    backgroundColor: "rgba(255,255,255,0.08)",
    "&:hover": {
      borderColor: "rgba(255,255,255,0.36)",
      backgroundColor: "rgba(255,255,255,0.14)",
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        p: { xs: 2.5, md: 3 },
        background: "linear-gradient(135deg, #13233a 0%, #1a3150 100%)",
        borderColor: "rgba(255,255,255,0.08)",
      }}
    >
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={7}>
          <Chip
            label="Built for collectors"
            size="small"
            sx={{
              mb: 1.75,
              fontWeight: 700,
              backgroundColor: "rgba(255,255,255,0.10)",
              color: "#ffffff",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          />
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 900,
              lineHeight: 1.08,
              color: "#ffffff",
              mb: 1,
              maxWidth: 640,
            }}
          >
            Track your entire collection in one place
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.75)",
              fontSize: { xs: "0.95rem", md: "1rem" },
              mb: 1.35,
              fontWeight: 500,
              lineHeight: 1.45,
            }}
          >
            Cards, sealed products, Funko Pops, LEGO, memorabilia, comics, games, and more—all in one place.
          </Typography>
          <Typography
            sx={{
              maxWidth: 620,
              color: "rgba(255,255,255,0.72)",
              mb: 2.25,
              fontSize: { xs: "0.98rem", md: "1.02rem" },
              lineHeight: 1.65,
            }}
          >
            Can't find an item? Add it manually or generate it instantly with AI. Every submission is reviewed by AI before being added to the site, then reviewed by our team to help maintain accuracy.
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mb: 1.15 }}>
            <Button
              component={RouterLink}
              to="/mycollection"
              variant="contained"
              sx={ctaButtonSx}
            >
              Start Tracking Free
            </Button>
            <Button
              component={RouterLink}
              to="/search?q=__all__"
              variant="contained"
              sx={ctaButtonSx}
            >
              Explore the Database
            </Button>
          </Stack>

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "flex-start",
              gap: 0.75,
              width: { xs: "100%", sm: "fit-content" },
              maxWidth: "100%",
              px: 1.25,
              py: 0.8,
              mb: 1.15,
              borderRadius: 2,
              color: "rgba(255,255,255,0.92)",
              backgroundColor: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <HomeIcon
              path={homeIconPaths.verified}
              aria-hidden="true"
              sx={{ mt: 0.1, fontSize: 17, flexShrink: 0, color: "#ffffff" }}
            />
            <Typography variant="caption" sx={{ color: "inherit", fontWeight: 700, lineHeight: 1.45 }}>
              No subscription required. Create an account and start tracking your collection for free.
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ mb: 2.25 }}
          >
            <Button
              component="a"
              href="https://www.instagram.com/thecollectorstop/"
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={socialButtonSx}
              startIcon={
                <Box
                  component="img"
                  src="/logos/instagram.svg"
                  alt=""
                  sx={{ width: 20, height: 20 }}
                />
              }
            >
              Instagram
            </Button>

            <Button
              component="a"
              href="https://www.facebook.com/profile.php?id=61561271679681"
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={socialButtonSx}
              startIcon={
                <Box
                  component="img"
                  src="/logos/facebook.svg"
                  alt=""
                  sx={{ width: 20, height: 20 }}
                />
              }
            >
              Facebook
            </Button>
          </Stack>

          <Grid container spacing={1} sx={{ maxWidth: 440 }}>
            {["Multi-category tracking", "AI-Assisted & Reviewed", "Search, scan, or add", "No subscription required"].map((badge) => (
              <Grid item xs={6} key={badge}>
                <Chip
                  label={badge}
                  variant="outlined"
                  sx={{
                    width: "100%",
                    justifyContent: "center",
                    backgroundColor: "rgba(255,255,255,0.06)",
                    borderColor: "rgba(255,255,255,0.12)",
                    color: "rgba(255,255,255,0.86)",
                    fontWeight: 600,
                    height: "auto",
                    minHeight: 32,
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      textAlign: "center",
                      py: 0.5,
                    },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
            sx={{
              height: "100%",
              borderRadius: 2,
              border: "1px solid rgba(255,255,255,0.12)",
              backgroundColor: "rgba(255,255,255,0.12)",
              p: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              gap: 1.35,
            }}
          >
            <Box sx={{ maxWidth: 360 }}>
              <Typography
                variant="overline"
                sx={{ color: "rgba(255,255,255,0.60)", letterSpacing: 1.1, fontWeight: 700 }}
              >
                Why collectors use it
              </Typography>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: 800,
                  color: "#ffffff",
                  mb: 0.75,
                  lineHeight: 1.25,
                }}
              >
                One home for everything you collect
              </Typography>
            </Box>

            <Stack spacing={1.05} sx={{ width: "100%", maxWidth: 360 }}>
              {collectorBenefits.map((benefit) => (
                <Box
                  key={benefit.title}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.15,
                    px: 1.35,
                    py: 1.2,
                    borderRadius: 1.75,
                    backgroundColor: "rgba(255,255,255,0.09)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    textAlign: "left",
                    transition: "transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 24px rgba(0,0,0,0.20)",
                      backgroundColor: "rgba(255,255,255,0.14)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      flexShrink: 0,
                      borderRadius: 1.25,
                      display: "grid",
                      placeItems: "center",
                      backgroundColor: "rgba(255,255,255,0.12)",
                    }}
                  >
                    <HomeIcon path={benefit.icon} sx={{ color: "#ffffff", fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 800, lineHeight: 1.25, mb: 0.25 }}>
                      {benefit.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.76)", lineHeight: 1.4 }}>
                      {benefit.detail}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}

function HowItWorksSection() {
  const steps = [
    { title: "Search", description: "Browse thousands of collectibles.", icon: homeIconPaths.search },
    { title: "Create", description: "Can't find it? Add it manually or generate it instantly with AI.", icon: homeIconPaths.create },
    { title: "AI + Team Review", description: "AI reviews every submission before it is added to the site. Our team then checks it for accuracy.", icon: homeIconPaths.verified },
    { title: "Collect", description: "Track, organize, value, and share your collection with no subscription required.", icon: homeIconPaths.collection },
  ];

  return (
    <SectionBlock title="How It Works" subtitle="Start with one item or build out your entire collection at your own pace.">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "minmax(0, 1fr) 24px minmax(0, 1fr) 24px minmax(0, 1fr) 24px minmax(0, 1fr)",
          },
          gap: { xs: 1.25, sm: 1.5, md: 0.75 },
          alignItems: "stretch",
        }}
      >
        {steps.map((step, index) => (
          <React.Fragment key={step.title}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.75, md: 1.5 },
                minWidth: 0,
                height: "100%",
                borderRadius: 2,
                borderColor: "rgba(12, 35, 64, 0.08)",
                background: "linear-gradient(145deg, #ffffff 0%, #f6f9fc 100%)",
                boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
                transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 16px 30px rgba(15, 23, 42, 0.12)",
                  borderColor: "rgba(18, 89, 156, 0.20)",
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 2,
                  mb: 1.25,
                  color: "primary.main",
                  backgroundColor: "rgba(18, 89, 156, 0.09)",
                }}
              >
                <HomeIcon path={step.icon} sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 850, color: "#10233f", mb: 0.55, lineHeight: 1.2 }}>
                {step.title}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
                {step.description}
              </Typography>
            </Paper>
            {index < steps.length - 1 && (
              <Box sx={{ display: { xs: "none", sm: "none", md: "grid" }, placeItems: "center", color: "primary.main" }} aria-hidden="true">
                <HomeIcon path={homeIconPaths.arrow} sx={{ fontSize: 22 }} />
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>
    </SectionBlock>
  );
}

function ImportPreviewSection() {
  return (
    <SectionBlock title="Already tracking your collection somewhere else?">
      <Stack spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "space-between", sm: "flex-start" },
            gap: { xs: 0.75, sm: 1.5 },
            p: { xs: 1.25, sm: 1.5 },
            borderRadius: 2,
            border: "1px solid rgba(12, 35, 64, 0.08)",
            background: "linear-gradient(135deg, #f8fbfe 0%, #eef4f9 100%)",
          }}
        >
          {[
            { label: "Spreadsheet", icon: homeIconPaths.spreadsheet },
            { label: "Collection App", icon: homeIconPaths.app },
          ].map((source) => (
            <Box key={source.label} sx={{ minWidth: 0, textAlign: "center" }}>
              <Box
                sx={{
                  width: { xs: 44, sm: 52 },
                  height: { xs: 44, sm: 52 },
                  mx: "auto",
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 2,
                  color: "primary.main",
                  backgroundColor: "#ffffff",
                  boxShadow: "0 6px 16px rgba(15, 23, 42, 0.08)",
                }}
              >
                <HomeIcon path={source.icon} sx={{ fontSize: { xs: 25, sm: 30 } }} />
              </Box>
              <Typography variant="caption" sx={{ display: "block", mt: 0.6, color: "text.secondary", fontWeight: 700 }}>
                {source.label}
              </Typography>
            </Box>
          ))}
          <HomeIcon path={homeIconPaths.arrow} sx={{ color: "primary.main", fontSize: { xs: 22, sm: 28 }, flexShrink: 0 }} aria-hidden="true" />
          <Box sx={{ textAlign: "center", minWidth: 0 }}>
            <Box
              sx={{
                width: { xs: 54, sm: 64 },
                height: { xs: 54, sm: 64 },
                display: "grid",
                placeItems: "center",
                borderRadius: 2,
                backgroundColor: "#ffffff",
                boxShadow: "0 8px 20px rgba(15, 23, 42, 0.10)",
                overflow: "hidden",
              }}
            >
              <Box component="img" src="/logos/LogoNew.png" alt="The Collector Stop" sx={{ width: "100%", height: "100%", objectFit: "contain", p: 0.5 }} />
            </Box>
            <Typography variant="caption" sx={{ display: "block", mt: 0.6, color: "#10233f", fontWeight: 800 }}>
              The Collector Stop
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.65, maxWidth: 760 }}>
          Whether you're using a spreadsheet or another collection tracker, we're building tools to make bringing your collection into The Collector Stop as simple as possible.
        </Typography>
        <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 700, lineHeight: 1.5 }}>
          You can start building your collection today without a subscription.
        </Typography>
        <Chip label="Collection Import Coming Soon" color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
        <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.65 }}>
          For now, search, scan, or add items directly to start building your collection.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ width: { xs: "100%", sm: "auto" } }}>
          <Button component={RouterLink} to="/mycollection" variant="contained" sx={{ fontWeight: 800 }}>
            Start Tracking Free
          </Button>
          <Button component={RouterLink} to="/add" variant="outlined" sx={{ fontWeight: 800 }}>
            Add an Item
          </Button>
        </Stack>
      </Stack>
    </SectionBlock>
  );
}

function CommunitySection() {
  return (
    <SectionBlock
      title="Community"
      subtitle="Connect with collectors, explore public collections, and keep up with friends without turning collecting into a follower-driven social network."
      action={
        <Button
          component={RouterLink}
          to="/accounts"
          variant="contained"
          sx={{ fontWeight: 800, whiteSpace: "nowrap" }}
        >
          Find Collectors
        </Button>
      }
    >
      <Grid container spacing={1.5}>
        {communityFeatures.map((feature) => (
          <Grid item xs={12} sm={4} key={feature.title}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.75,
                height: "100%",
                borderRadius: 2,
                borderColor: "rgba(12, 35, 64, 0.08)",
                backgroundColor: "#f9fbfd",
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#10233f", mb: 0.75 }}>
                {feature.title}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55 }}>
                {feature.detail}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </SectionBlock>
  );
}

function SiteStatsSection({ counts, err }) {
  return (
    <Paper sx={{ ...panelSx, backgroundColor: "#ffffff" }} elevation={0}>
      <Typography variant="h6" component="h2" sx={{ fontWeight: "bold", color: "primary.main", mb: 1.25 }}>
        Site Stats
      </Typography>

      {err ? (
        <Typography color="error">Could not load stats - {err}</Typography>
      ) : !counts ? (
        <Typography color="text.secondary">Loading...</Typography>
      ) : (
        <Stack spacing={1.2}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1.2,
              borderRadius: 1.75,
              backgroundColor: "#f8fafc",
              border: "1px solid rgba(12, 35, 64, 0.06)",
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Collectors
            </Typography>
            <Typography variant="h6" sx={{ color: "#10233f", fontWeight: 800 }}>
              {Number(counts.users ?? 0).toLocaleString()}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1.2,
              borderRadius: 1.75,
              backgroundColor: "#f8fafc",
              border: "1px solid rgba(12, 35, 64, 0.06)",
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Collectibles in the Database
            </Typography>
            <Typography variant="h6" sx={{ color: "#10233f", fontWeight: 800 }}>
              {Number(counts.items ?? 0).toLocaleString()}
            </Typography>
          </Box>
        </Stack>
      )}
    </Paper>
  );
}

function QuickLinksSection({ theme }) {
  return (
    <Paper sx={{ ...panelSx, backgroundColor: "#ffffff" }} elevation={0}>
      <Typography variant="h6" component="h2" sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}>
        Browse Categories
      </Typography>
      <Grid container spacing={1.5}>
        {quickLinks.map((link) => (
          <Grid item xs={6} sm={4} md={12} key={link.title}>
            <Box
              component={RouterLink}
              to={link.path}
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
                minHeight: { xs: 100, md: 108 },
                textAlign: "center",
                textDecoration: "none",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "rgba(12, 35, 64, 0.08)",
                bgcolor: "#f8fafc",
                overflow: "hidden",
                transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                ...(link.image && {
                  backgroundImage: `url(${link.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    bgcolor: "rgba(8,18,32,0.42)",
                    zIndex: 0,
                  },
                }),
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: theme.shadows[4],
                  borderColor: "rgba(18, 89, 156, 0.22)",
                },
              }}
            >
              <Typography
                variant="button"
                sx={{
                  position: "relative",
                  zIndex: 1,
                  color: link.image ? "#fff" : "text.primary",
                  textShadow: link.image ? "0 2px 8px rgba(0,0,0,0.45)" : "none",
                  fontWeight: 700,
                }}
              >
                {link.title}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

function SetsSection() {
  return (
    <SectionBlock
      title="Sets"
      subtitle="Browse organized set pages and find items to add to your collection."
    >
      <Grid container spacing={1.5}>
        {setsLinks.map((set) => (
          <Grid item xs={12} key={set.title}>
            <Box
              component={RouterLink}
              to={set.path}
              sx={{
                ...interactiveCardSx,
                overflow: "hidden",
                backgroundColor: "#f9fbfd",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  height: { xs: 160, sm: 200 },
                  width: "100%",
                  backgroundColor: "#eaf0f6",
                  overflow: "hidden",
                  borderBottom: "1px solid rgba(12, 35, 64, 0.08)",
                }}
              >
                <ImageWithFallback
                  src={set.image}
                  alt={set.title}
                  placeholderSrc="/logos/default-item.jpg"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center center",
                    display: "block",
                  }}
                />
              </Box>

              <Box
                sx={{
                  p: 1.5,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    color: "#10233f",
                    textAlign: "center",
                  }}
                >
                  {set.title}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </SectionBlock>
  );
}

function TrendingEventsSection() {
  return (
    <SectionBlock
      title="Trending Events"
      subtitle="Collector events, championship moments, and releases shaping interest across the hobby."
    >
      <Grid container spacing={1.5}>
        {trendingEvents.map((event) => (
          <Grid item xs={12} sm={6} key={event.title}>
            <Box
              component={event.isDisabled ? "div" : RouterLink}
              to={event.isDisabled ? undefined : event.path}
              sx={{
                ...interactiveCardSx,
                p: 1.75,
                opacity: event.isDisabled ? 0.68 : 1,
                cursor: event.isDisabled ? "not-allowed" : "pointer",
                transition: "all 0.18s ease",
                ...(event.isDisabled ? {} : {
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 16px 32px rgba(15, 23, 42, 0.12)",
                    borderColor: "rgba(18, 89, 156, 0.35)",
                    backgroundColor: "#ffffff"
                  }
                }),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 1.15,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, color: "#10233f", lineHeight: 1.25 }}
                >
                  {event.title}
                </Typography>
                <Chip
                  label={event.isDisabled ? "Coming Soon" : "Open"}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontWeight: 700,
                    backgroundColor: "#fff",
                    borderColor: "rgba(12, 35, 64, 0.10)",
                    color: "#10233f",
                    opacity: event.isDisabled ? 0.9 : 1,
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55 }}>
                {event.detail}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </SectionBlock>
  );
}

function TrendingItemsSection({ items, loading, error }) {
  const { showPrices: pricePreferenceEnabled, pricesLoaded } = usePriceVisibility();
  const showPrices = pricesLoaded && pricePreferenceEnabled;
  return (
    <SectionBlock
      title="Trending Items"
      subtitle="Collectibles currently seeing notable market movement across the database."
    >
      {loading ? (
        <Typography color="text.secondary">Loading trending items...</Typography>
      ) : error ? (
        <Typography color="error">Could not load trending items - {error}</Typography>
      ) : !items.length ? (
        <Typography color="text.secondary">No trending items available right now.</Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            },
            gap: 1.5,
          }}
        >
          {items.slice(0, 6).map((item) => (
            <Box
              key={item.item_id}
              component={RouterLink}
              to={buildItemUrl(item)}
              sx={{
                ...interactiveCardSx,
                p: { xs: 1.5, md: 1.75 },
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  height: 110,
                  borderRadius: 1.75,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "rgba(12, 35, 64, 0.08)",
                  backgroundColor: "#eef3f8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {item?.image_url ? (
                  <ImageWithFallback
                    src={item.image_url}
                    alt={item.name || "Trending item"}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                ) : (
                  <PlaceholderMedia label="Item Preview" height={110} />
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, lineHeight: 1.25, color: "#10233f", minWidth: 0 }}
                >
                  {item.name}
                </Typography>
                {showPrices && <Chip
                  label={item.direction === "up" ? "Rising" : item.direction === "down" ? "Down" : "Flat"}
                  size="small"
                  color={item.direction === "up" ? "success" : item.direction === "down" ? "error" : "default"}
                  sx={{ fontWeight: 700, flexShrink: 0 }}
                />}
              </Box>
              {showPrices && <Typography variant="h6" sx={{ color: "primary.main", fontWeight: 800 }}>
                {formatTrendingPrice(item)}
              </Typography>}
              {showPrices && formatChange(item) ? (
                <Typography variant="body2" color="text.secondary">
                  {formatChange(item)}
                </Typography>
              ) : item?.category ? (
                <Typography variant="body2" color="text.secondary">
                  {item.category}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      )}
    </SectionBlock>
  );
}

function NewReleasesSection() {
  return (
    <SectionBlock
      title="New Releases"
      subtitle="Recently added products and releases collectors can explore and track."
    >
      <Grid container spacing={1.5}>
        {newReleases.map((release) => (
          <Grid item xs={12} sm={6} key={release.title}>
            <Paper
              {...(release.path
                ? {
                    component: RouterLink,
                    to: release.path,
                  }
                : {})}
              variant="outlined"
              sx={{
                p: 1.75,
                borderRadius: 2,
                height: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                borderColor: "rgba(12, 35, 64, 0.08)",
                backgroundColor: "#f9fbfd",
                textDecoration: "none",
                color: "inherit",
                ...(release.path
                  ? {
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#ffffff" },
                    }
                  : {}),
              }}
            >
              <Box sx={{ width: 68, flexShrink: 0 }}>
                <Box
                  sx={{
                    height: 68,
                    borderRadius: 1.75,
                    border: "1px solid",
                    borderColor: "rgba(12, 35, 64, 0.08)",
                    backgroundColor: "#eef3f8",
                    overflow: "hidden",
                  }}
                >
                  {release.image ? (
                    <Box
                      component="img"
                      src={release.image}
                      alt={release.title}
                      onError={(e) => {
                        e.currentTarget.src = "/logos/default-item.jpg";
                      }}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 2,
                        display: "block",
                      }}
                    />
                  ) : (
                    <PlaceholderMedia label="Drop" height={68} />
                  )}
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.4, color: "#10233f" }}>
                  {release.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {release.subtitle}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </SectionBlock>
  );
}

function UpcomingSection() {
  return (
    <SectionBlock
      title="Upcoming"
      subtitle="Upcoming conventions, products, and collecting moments worth watching."
    >
      <Stack divider={<Divider flexItem />} spacing={0}>
        {upcomingItems.map((entry) => (
          <Box
            key={entry.title}
            sx={{
              py: 1.5,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.25, color: "#10233f" }}>
                {entry.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {entry.note}
              </Typography>
            </Box>
            <Chip
              label={entry.when}
              variant="outlined"
              sx={{
                alignSelf: { xs: "flex-start", sm: "center" },
                fontWeight: 700,
                backgroundColor: "#fff",
              }}
            />
          </Box>
        ))}
      </Stack>
    </SectionBlock>
  );
}

const Home = () => {
  usePageTitle();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [counts, setCounts] = useState(null);
  const [err, setErr] = useState("");
  const [trendingItems, setTrendingItems] = useState([]);
  const [trendingItemsError, setTrendingItemsError] = useState("");
  const [trendingItemsLoading, setTrendingItemsLoading] = useState(true);
  const firstFetch = useRef(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setErr("");
        const { data } = await api.get("/api/site-stats");
        if (!mounted) return;
        setCounts(data);
        if (firstFetch.current) firstFetch.current = false;
      } catch (e) {
        if (!mounted) return;
        setErr(e?.message || "Failed to load stats");
      }
    };

    load();
    const id = setInterval(load, 60_000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadTrendingItems = async () => {
      try {
        setTrendingItemsLoading(true);
        setTrendingItemsError("");
        const { data } = await api.get("/api/trending-items");
        if (!mounted) return;
        setTrendingItems(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        if (!mounted) return;
        setTrendingItemsError(e?.response?.data?.error || e?.message || "Failed to load trending items");
      } finally {
        if (mounted) setTrendingItemsLoading(false);
      }
    };

    loadTrendingItems();
    return () => {
      mounted = false;
    };
  }, []);

  const introSection = <IntroFeaturedSection />;
  const howItWorksSection = <HowItWorksSection />;
  const importPreviewSection = <ImportPreviewSection />;
  const statsSection = <SiteStatsSection counts={counts} err={err} />;
  const quickLinksSection = <QuickLinksSection theme={theme} />;
  const communitySection = <CommunitySection />;
  const setsSection = <SetsSection />;
  const trendingEventsSection = <TrendingEventsSection />;
  const trendingItemsSection = (
    <TrendingItemsSection
      items={trendingItems}
      loading={trendingItemsLoading}
      error={trendingItemsError}
    />
  );
  const newReleasesSection = <NewReleasesSection />;
  const upcomingSection = <UpcomingSection />;

  return (
    <AppShell>
      <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 1, md: 1.5 }, backgroundColor: "transparent" }}>
        {isDesktop ? (
          <Grid container spacing={{ xs: 2, md: 2.5 }} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, md: 2.5 } }}>
                {introSection}
                {howItWorksSection}
                {importPreviewSection}
                {communitySection}
                {trendingItemsSection}
                {trendingEventsSection}
                {newReleasesSection}
                {upcomingSection}
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: { xs: 2, md: 2.5 },
                  position: { md: "sticky" },
                  top: { md: "calc(var(--tcs-header-offset, 120px) + 24px)" },
                }}
              >
                {statsSection}
                {quickLinksSection}
                {setsSection}
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {introSection}
            {howItWorksSection}
            {importPreviewSection}
            {communitySection}
            {statsSection}
            {quickLinksSection}
            {setsSection}
            {trendingItemsSection}
            {trendingEventsSection}
            {newReleasesSection}
            {upcomingSection}
          </Box>
        )}
      </Box>
    </AppShell>
  );
};

export default Home;

